import { KeyboardEvent, useState } from "react";

interface NumericChipInputProps {
  label: string;
  values: number[];
  onChange: (values: number[]) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}

const parseNumericIds = (raw: string): number[] => {
  return raw
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => Number(part))
    .filter((value) => Number.isInteger(value) && value > 0);
};

const NumericChipInput = ({
  label,
  values,
  onChange,
  placeholder = "Type an ID and press Enter",
  helperText,
  disabled = false,
}: NumericChipInputProps) => {
  const [inputValue, setInputValue] = useState("");

  const addIds = (incoming: number[]) => {
    if (incoming.length === 0) return;
    const merged = [...values];
    incoming.forEach((id) => {
      if (!merged.includes(id)) {
        merged.push(id);
      }
    });
    onChange(merged);
  };

  const commitInput = () => {
    addIds(parseNumericIds(inputValue));
    setInputValue("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
      if (inputValue.trim()) {
        event.preventDefault();
        commitInput();
      }
    }
    if (event.key === "Backspace" && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-col w-full">
      <span className="text-text-base text-[10px] font-semibold pb-[6px]">
        {label}
      </span>
      <div
        className={`flex flex-wrap items-center gap-[6px] min-h-[42px] px-[8px] py-[6px] border border-border-subtle rounded-lg bg-transparent ${
          disabled ? "opacity-50" : ""
        }`}
      >
        {values.map((id) => (
          <span
            key={id}
            className="flex items-center gap-[4px] border border-border-button-secondary bg-bg-button-secondary rounded-full px-[8px] py-[2px] text-[10px] font-medium text-text-base"
          >
            {id}
            <button
              type="button"
              className="text-[12px] leading-none text-text-base"
              disabled={disabled}
              onClick={() => onChange(values.filter((value) => value !== id))}
              aria-label={`Remove ${id}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          inputMode="numeric"
          value={inputValue}
          disabled={disabled}
          placeholder={values.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent text-[10px] text-text-base outline-none placeholder:text-text-header"
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitInput}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text");
            const parsed = parseNumericIds(pasted);
            if (parsed.length > 0) {
              event.preventDefault();
              addIds(parsed);
              setInputValue("");
            }
          }}
        />
      </div>
      {helperText && (
        <span className="text-text-header text-[10px] mt-[4px]">
          {helperText}
        </span>
      )}
    </div>
  );
};

export default NumericChipInput;
